import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE } from '../content/site';
import './LegalPage.css';
import MailLink from './MailLink';

/**
 * Shared chrome for the policy pages: title block, sticky contents rail that
 * tracks the section in view, and a print-friendly body.
 *
 * `sections` is [{ id, title, content }] — content is JSX.
 */
function LegalPage({
  title,
  lede,
  applies,
  sections,
  updated = SITE.legalUpdated,
  showAddress = false,
}) {
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const nodes = sections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);
    if (!nodes.length || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <main id="main" className="legal">
      <div className="legal-hero">
        <div className="legal-glow" aria-hidden="true" />
        <div className="wrap">
          <nav className="crumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>{title}</span>
          </nav>
          <h1>{title}</h1>
          <p className="legal-meta">
            Last updated {updated}
            {applies ? ` · ${applies}` : ''}
          </p>
          {lede ? <p className="legal-lede">{lede}</p> : null}
        </div>
      </div>

      <div className="wrap legal-body">
        <aside className="toc">
          <p className="toc-title">On this page</p>
          <ul>
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={active === section.id ? 'active' : undefined}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
          <div className="toc-foot">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/delete-account">Delete account</Link>
          </div>
        </aside>

        <article className="legal-content">
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="legal-section">
              <h2>{section.title}</h2>
              {section.content}
            </section>
          ))}

          <div className="legal-contact callout">
            <p>
              <strong>Questions about this page?</strong> Write to{' '}
              <MailLink subject="Policy question" address={showAddress}>
                the studio
              </MailLink>{' '}
              and we answer within 30 days — in practice, within two working
              days.
            </p>
          </div>
        </article>
      </div>
    </main>
  );
}

export default LegalPage;
